import { FocusEvent, KeyboardEvent, MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { pushModalLayer } from '../lib/modalLayers';
import { useLatest } from './useLatest';

/** Сколько взведённая кнопка ждёт второе нажатие, мс. */
export const DOUBLE_PRESS_TIMEOUT_MS = 3000;

/**
 * Нажатие раньше этого срока после взвода действие не выполняет, кнопка остаётся взведённой: двойной щелчок «по
 * привычке» и дважды стукнутый Enter не должны удалять запись — вопрос сначала надо увидеть.
 */
export const DOUBLE_PRESS_GUARD_MS = 400;

export interface IDoublePressOptions<E extends HTMLElement> {
    /** Второе нажатие. Вернёт промис — до его завершения `pending` (кнопка занята). */
    onConfirm: (event: MouseEvent<E>) => unknown;
    /** Первое нажатие: кнопка взведена. */
    onArm?: (event: MouseEvent<E>) => void;
    /** Недоступна: не взводится, взведённая — сбрасывается. */
    disabled?: boolean;
    /** Занята (например, грузится пояснение): взводится, но второе нажатие не выполняет. */
    busy?: boolean;
    /** Сколько ждёт второе нажатие, мс. @default 3000 */
    timeout?: number;
    /** Взвод и сброс — например, чтобы держать видимой кнопку строки, пока она взведена. */
    onArmedChange?: (armed: boolean) => void;
}

export interface IDoublePress<E extends HTMLElement, R extends HTMLElement> {
    armed: boolean;
    /** Идёт действие второго нажатия (промис `onConfirm`). */
    pending: boolean;
    /** Корень кнопки: нажатие вне него сбрасывает взвод, он же — слой Escape в стеке окон. */
    rootRef: RefObject<R | null>;
    /** Обработчики кнопки-триггера. */
    triggerProps: {
        onClick: (event: MouseEvent<E>) => void;
        onBlur: (event: FocusEvent<E>) => void;
        onKeyDown: (event: KeyboardEvent<E>) => void;
    };
    disarm: () => void;
}

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
    !!value && typeof (value as PromiseLike<unknown>).then === 'function';

/**
 * Кнопка в два нажатия: первое взводит её («Точно удалить?»), второе выполняет действие. Взвод сбрасывается сам через
 * `timeout`, по уходу фокуса, по нажатию вне кнопки и по Escape — Escape идёт через стек окон (`lib/modalLayers`):
 * взведённая кнопка в модалке сбрасывается, а модалка остаётся открытой.
 */
export const useDoublePress = <E extends HTMLElement = HTMLButtonElement, R extends HTMLElement = E>({
    onConfirm,
    onArm,
    disabled = false,
    busy = false,
    timeout = DOUBLE_PRESS_TIMEOUT_MS,
    onArmedChange,
}: IDoublePressOptions<E>): IDoublePress<E, R> => {
    const [armed, setArmed] = useState(false);
    const [pending, setPending] = useState(false);
    const rootRef = useRef<R | null>(null);
    // состояние для обработчиков событий — без ожидания перерисовки между двумя быстрыми нажатиями
    const armedRef = useRef(false);
    const armedAt = useRef(0);
    const latest = useLatest({ onConfirm, onArm, onArmedChange });

    const change = useCallback(
        (next: boolean) => {
            if (armedRef.current === next) return;
            armedRef.current = next;
            if (next) armedAt.current = Date.now();
            setArmed(next);
            latest.current.onArmedChange?.(next);
        },
        [latest],
    );
    const disarm = useCallback(() => change(false), [change]);

    useEffect(() => {
        if (disabled || pending) disarm();
    }, [disabled, pending, disarm]);

    useEffect(() => {
        if (!armed) return undefined;
        const timer = window.setTimeout(disarm, timeout);
        const onPointerDown = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) disarm();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        const popLayer = pushModalLayer({
            root: () => rootRef.current,
            dialog: () => rootRef.current,
            anchored: true,
            onEscape: () => {
                disarm();
                return true;
            },
        });
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('pointerdown', onPointerDown, true);
            popLayer();
        };
    }, [armed, timeout, disarm]);

    const onClick = (event: MouseEvent<E>) => {
        if (disabled || pending) return;
        if (!armedRef.current) {
            change(true);
            latest.current.onArm?.(event);
            return;
        }
        if (busy || Date.now() - armedAt.current < DOUBLE_PRESS_GUARD_MS) return;
        change(false);
        const result = latest.current.onConfirm(event);
        if (isPromiseLike(result)) {
            setPending(true);
            const done = () => setPending(false);
            result.then(done, done);
        }
    };

    const onBlur = (event: FocusEvent<E>) => {
        if (rootRef.current?.contains(event.relatedTarget)) return;
        disarm();
    };

    const onKeyDown = (event: KeyboardEvent<E>) => {
        if (!armedRef.current) return;
        // Escape с фокусом на кнопке гасим здесь: обёртка с остановкой всплытия (шапка таблицы) не пустила бы его
        // до стека окон, а окно вокруг кнопки закрываться не должно
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            disarm();
        }
        // зажатый Enter повторяет нажатие кнопки — автоповтор не подтверждает
        if (event.key === 'Enter' && event.repeat) event.preventDefault();
    };

    return { armed, pending, rootRef, triggerProps: { onClick, onBlur, onKeyDown }, disarm };
};
